/**
 * LangSlice.js
 * 
 * This file contains Redux slice for managing language data in the frontend application.
 * 
 * The slice defines the initial state, reducers, and actions for managing language data.
 */
import { createSlice } from "@reduxjs/toolkit";

export const langSlice = createSlice({
  name: "lang",
  initialState: {
    value: {
      currLang: "en",
      langOptions: {
        he: "עברית",
        en: 'english',
      },
      pageDir: {
        he: "rtl",
        en: 'ltr',

      },
      loginOptions: {
        login: {
          en: "login",
          he: "כניסה"
        },
        logout: {
          en: "logout",
          he: "התנתק"
        },

        profile: {
          en: "profile",
          he: "אזור אישי"
        },

      },

      pageOptions: {
        "realtime": {
          en: "Real-Time Monitor",
          he: "מוניטור זמן אמת"
        },
        "operationalerts": {
          en: "Operation Alerts",
          he: "התראות זמן אמת"
        },
        "ridership": {
          en: "Ridership",
          he: "נוסעים"
        },

        "service": {
          en: "Service Insights",
          he: "מדדי שרות"
        },
        "network": {
          en: "Network Viewer",
          he: "סייר רשת תחבורה"
        },


      },

      pageTitles: {
        "realtime": {
          en: "Real-Time Transit Monitor",
          he: 'ניטור תח"צ זמן אמת'
        },
        "ridership": {
          en: "Ridership",
          he: "נוסעים"
        },
        "operationalerts": {
          en: "Operation Alerts",
          he: "התראות תפעוליות בזמן אמת"
        },
        "service": {
          en: "Service Insights",
          he: "מדדי שרות"
        },
        "network": {
          en: "Network Viewer",
          he: "סייר רשת תחבורה"
        },

        "profile": {
          en: "Profile",
          he: "אזור אישי"
        },


      },

      pageDescs: {
        "realtime": {
          en: "",
          he: "",
        },
        "ridership": {
          en: "",
          he: "",
        },
        "operationalerts": {
          en: "",
          he: "",
        },
        "service": {
          en: "",
          he: "",
        },
        "network": {
          en: "",
          he: "",
        },

        "profile": {
          en: "",
          he: ""
        },


      },
      itemInfoTitles: {
        fullScreen: {
          en: "Enter full screen",
          he: "מסך מלא"
        },
        exitFullScreen: {
          en: "Exit Full screen",
          he: "יציאה ממסך מלא"
        },
        reCenter: {
          en: "Re-Center View",
          he: "מרכז תצוגה מחדש"
        },

      },

      controlPanelOptions: {
        title: {
          en: "Control Panel",
          he: "לוח בקרה"
        },
        showPanel: {
          en: "Show control panel",
          he: "הצג לוח בקרה"
        },
        hidePanel: {
          en: "Hide control panel",
          he: "הסתר לוח בקרה"
        },
        vehicles: {
          en: "Vehicles",
          he: "כלי רכב"
        },
        showVehicles: {
          en: "Show vehicles",
          he: "הצג כלי רכב"
        },
        hideVehicles: {
          en: "Hide vehicles",
          he: "הסתר כלי רכב"
        },
        networkOpacity: {
          en: "Road network opacity",
          he: "שקיפות רשת הכבישים"
        },

      },

      roadSectionPopup: {
        title: {
          en: "Road Section",
          he: "מקטע כביש"
        },
        avgSpeed: {
          en: "Average Speed",
          he: "מהירות ממוצעת"
        },
        volume: {
          en: "Vehicle Volume",
          he: "נפח תנועה"
        },
        noData: {
          en: "No data",
          he: "אין נתונים"
        },

      },

      localeOptions: {
        "Israel": {
          en: "Israel",
          he: "ישראל"
        },
        "New York City": {
          en: "New York City",
          he: "ניו יורק"
        }

      },
      spaceTimeAxisLabels: {
        x: { en: "Time", he: "שעה" },
        y: { en: "Distance Traveled", he: "מרחק לאורך המסלול" },

      },
      operationAlertsOptions: {
        buttonOptions: {
          "headways": {
            en: "Headways",
            he: "מרווח נסיעות"
          },
          "delays": {
            en: "Delays",
            he: "איחורים"
          }
        },

        chartTitles: {
          "headways": {
            en: "Headways",
            he: "מרווח בין נסיעות"
          },
          "delays": {
            en: "Delays",
            he: "מיקום נסיעה ביחס לתכנון"
          }
        },
        aboutText: {
          "headways": {
            en: "Headways",
            he: `מיקום הרכבים בזמן אמת:
            \n
            כל שורה מייצגת קו-כיוון וניתן לראות עליה את הרכבים המבצעים כעת נסיעה.
            \n
            הקו עליו הם נוסעים מראה לנו את מהירות הנסיעה הממוצעת בשעה הזו של היום במקטעי הדרך השונים לאורך מסלול הקו.
            \n`,

          },
          "delays": {
            en: "Delays",
            he: ""
          }
        },



        slidersTitles: {
          "range": {
            "time": {
              en: "Delay Range (min.)",
              he: "שיעור איחור (דק')"
            },
            "space": {
              en: "Delay Range (km.)",
              he: "שיעור פיגור (קמ')"
            },
          },
          "start": {
            en: "Trip Start Range (min.)",
            he: "טווח התחלת נסיעה על פי תכנון(דק')"
          },

          "end": {
            en: "Trip End Range (min.)",
            he: "טווח סיום נסיעה על פי תכנון(דק')"
          }
        },
        delayTypeRadioTitle: {
          en: "Show Delays by:",
          he: "הצגת איחורים לפי:"

        },

        delayTypeRadioOptions: {
          time: { en: "Time", he: "זמן" },
          space: { en: "Location", he: "מיקום" },

        },

        delayAxisRadioTitle: {
          en: "Delay Axis",
          he: "תצוגת ציר X:"

        },
        delayAxisRadioOptions: {
          relative: { en: "Relative", he: "יחסי" },
          absolute: { en: "Absolute", he: "אבסולוטי" },

        },
        delaysSortAbout: {
          en: "Sort by",
          he: `ניתן להציג את תמונת האיחורים לפי שעת תחילת הנסיעה בסדר עוקב,
           לפי שיעור האיחור (מהנסיעה המאחרת ביותר לנסיעה המקדימה ביותר ביחס לתכנון) או לפי מספר קו וכיוון.` ,


        },
        headwaysAxisLabels: {
          distance: {
            en: "Distance Traveled",
            he: "מרחק לאורך המסלול"
          },
          line: { en: "Absolute", he: "אבסולוטי" },

        },

        delaysAxisLabels: {
          space: {
            x: {
              absolute: {
                en: "Distance Traveled",
                he: "מרחק לאורך המסלול"
              },
              relative: {
                en: "Distance Traveled Relative to Plan",
                he: "מרחק ביחס לתכנון"
              },
            },
            y: {
              absolute: { en: "Trips", he: "נסיעות" },
              relative: { en: "Trips", he: "נסיעות" },
            },

          },
          time: {
            x: {
              absolute: { en: "Time", he: "שעה" },
              relative: {
                en: "Delay Relative to Plan",
                he: "איחור/הקדמה ביחס לתכנון"
              },
            },
            y: {
              absolute: { en: "Trips", he: "נסיעות" },
              relative: { en: "Trips", he: "נסיעות" },
            },

          },
        },
        delaysLegend: {
          desc:
          {
            he: [`מיקום הרכב`, "מיקום מתוכנן"],
            en: ["Vehicle Location", "Planned Location"],
          },
          delayStatus:
          {
            he: ["מאחר", "מקדים", "בזמן"],
            en: ["Late", "Early", "On Time"],
          },
        }
      },
      realTimeLegends: {
        speed: {
          en: "Speed",
          he: 'מהירות',
        },
        speedUnitsIL: {
          en: "kmh",
          he: 'קמ"ש',

        },
        speedUnitsNYC: {
          en: "mph",
          he: 'מ"ש',

        },
        direction: {
          en: "Azimuth (deg)",
          he: 'אזימוט (מעלות)',
        },
        distance: {
          en: "Distance Traveled (km)",
          he: "מרחק מתחילת המסלול",
        },
        distanceUnitsIL: {
          en: "km",
          he: "קמ'",

        },
        distanceUnitsNYC: {
          en: "miles",
          he: "מייל",

        },

      },
      spaceTimeTitle: {
        en: "Space-Time Chart, Line:",
        he: "גרף מרחב זמן, קו: ",

      },
      spaceTimeAbout: {
        en: "Space-Time Chart",
        he: `גרף מרחב-זמן 
        \n 
        כל קו בגרף מציין נסיעה שבוצעה או עדיין מתבצעת.
        \n
        ציר ה-Y מתייחס לאורך מסלול הקו וציר ה-X למימד הזמן.
        קו האורך השחור בצד ימין מציין את נקודת הזמן הנוכחית.
        הגרף מתעדכן בזמן אמת.
        \n
        צבע הקווים של הנסיעות הפעילות תואם את צבע האוטובוסים המוצגים (בבחירת הצגה לפי "נסיעה").
        \n
        קו "נעלם" באמצע? כנראה שאיכון המיקום שלו הפסיק לשדר.
        \n`,

      },

      spaceTimePopup: {
        tripid: {
          en: "Trip Id",
          he: "מזהה נסיעה",
        }

      },


      realTimePanelTitles: {
        vizParamRadio: {
          title: {
            en: "Variable to Display:",
            he: "הצגה לפי:"
          },
          options: {
            operatorref: {
              en: "Operator",
              he: "מפעיל"

            },
            velocity: {
              en: "Speed",
              he: "מהירות"

            },
            bearing: {
              en: "Direction",
              he: "כיוון"

            },
            trip: {
              en: "Trip",
              he: "נסיעה"

            },
            distancefromstop: {
              en: "Distance Traveled",
              he: "מרחק"

            },
          }

        },
        speedSlider: {
          title: {
            en: "Speed",
            he: "מיון לפי מהירויות"
          },

          about: {
            en: "Based on SIRI data.",
            he: `מהירות זמן אמת מבוססת על חישוב לפי איכוני מיקומי האוטובוסים.`
          },

        },

        operatorSelection: {
          title: {
            en: "Select Operator",
            he: "בחירת מפעיל"
          },

        },

        lineSelection: {
          title: {
            en: "Select Line",
            he: "בחירת קו"
          },

        },

        clusterSelection: {
          title: {
            en: "Select Cluster",
            he: "בחירת אשכול"
          },

        },
        locnameSelection: {
          title: {
            en: "Select Locality",
            he: "בחירת ישוב"
          },
        },

        sortSelection: {
          title: {
            en: "Sort By",
            he: "מיון לפי"
          },

          options: {
            start: {
              en: "Trip Start",
              he: "תחילת נסיעה"
            },
            delay: {
              en: "Delay",
              he: "שיעור איחור",

            },
            route: {
              en: "Line",
              he: "קו",

            },
          },

        },

        clearSelection: {
          en: "Clear Selection",
          he: "נקה בחירה"

        },
        calendar: {
          titles: {
            sectionTitle: {
              en: "Time Span",
              he: "טווח זמן"
            },
            timeSliderTitle: {
              en: "Time",
              he: "שעה"

            },
            calendarWidgetTitle: {
              en: "Date",
              he: "תאריך"

            },
          },
          options: {
            realtime: {
              en: "Real-Time",
              he: "זמן אמת"

            },
            week: {
              en: "Past Week",
              he: "שבוע חולף"

            },

          },


        },

        trafficLightPriority: {
          titles: {
            sliderTitle: {
              en: "Traffic Light Priority",
              he: "הפעל התראת באנצ'ינג:"
            },

          },
          sliderStartLabel: {

            en: "20%",
            he: "פער קל"
          },
          sliderEndLabel: {
            en: "Bunching",
            he: "התלכדות נסיעות"



          },
          aboutText: {
            en: "Bunching",
            he: `התראת באנצ'ינג -
בחירה באפשרות זו תאפשר להציג סימון אוטובוסים שמצמצמים פער בין נסיעות עוקבות (על פי זמן ומרחק) באותו קו וכיוון.
\n
הערך המוצג מתייחס לאחוז צמצום הפער, ככל שישנו.
פער תקין בין נסיעות עומד על 0%.
\n
בחירה ב-20% תציג נסיעות שמתחילות להתקרב זה לזו, ובחירה ב-100% תציג נסיעות שכבר התלכדו. 
\n
ההתראה מוצגת באמצעות נקודה מהבהבת על המפה.
\n`




          },


        },
      },

      toopTipExplain: {

      },

      transitDataDict: {

        route_short_name: {
          en: "Line Number",
          he: "מספר קו",

        },
        directionref: {
          en: "Direction",
          he: "כיוון",

        },
        originaimeddeparturetime: {
          en: "Planned Departure",
          he: "שעת יציאה מתוכננת",

        },
        vehicleref: {
          en: "License Plate",
          he: "לוחית רישוי",

        },
        operatorref: {
          en: "Operator",
          he: "מפעיל",

        },
        distancefromstop: {
          en: "Distance Traveled",
          he: "מרחק מתחילת המסלול",

        },
        dist_diff: {
          en: "Distance From Following Trip",
          he: "מרחק מנסיעה עוקבת",

        },
        expected_dist_diff_m: {
          en: "Expected Distance from Following Trip",
          he: "מרחק צפוי מנסיעה עוקבת",

        },
        recordedattime: {
          en: "Recorded At",
          he: "שעת דיווח מיקום אחרונה",

        },
        time_diff: {
          en: "Current Frequency",
          he: "תדירות נוכחית",

        },
        route_desc: {
          en: "Route Desc",
          he: "קו-כיוון-חלופה",

        },

        route_id: {
          en: "Route Id",
          he: "מזהה קו",

        },
        stop_name: {
          en: "Stop Name",
          he: "שם תחנה"

        },
        stop_code: {
          en: "Stop Code",
          he: "מזהה תחנה",

        },
        velocity: {
          en: "Speed",
          he: "מהירות",

        },
        bearing: {
          en: "Bearing",
          he: "אזימוט",

        },
        length: {
          en: "Length",
          he: "אורך",

        },

        freq: {
          en: "Current Frequency (minutes)",
          he: "תדירות (דקות)",

        },

        dailyFreq: {
          en: "Daily Frequency",
          he: "תדירות יומית",

        },
        currLocation: {
          en: "Current Location",
          he: "מיקום נכחי",

        },

        currExpectedLocation: {
          en: "Current Expected Location",
          he: "מיקום צפוי",

        },

        currDelay: {
          en: "Current Delay",
          he: "איחור נכחי",

        },


      },
      tooltipExplain: {
        searchStopTooltip: {
          en: "Type a stop id or enter free text",
          he: 'חיפוש לפי מק"ט תחנה או טקסט חופשי',
        },
        searchRouteTooltip: {
          en: "Type a stop id or enter free text",
          he: 'חיפוש לפי מזהה קו, מק"ט או טקסט חופשי',
        },

        areaSelectionTooltip: {
          en: "Click on the map to select an area or select a cluster from the list",
          he: "הקלק על המפה לבחירת ישוב או בחר אשכול מהרשימה",
        },

        symbologyTooltip: {
          en: "Select a variable to display",
          he: "בחר משתנה להצגה",
        },

        delayTypeTooltip: {
          en: "Delays can be represented with respect to distance or time",
          he: `אפשרויות תצוגה על פי מיקום הנסיעה על ציר הזמן או על ציר המרחק, ביחס לתכנון הקווים`,
        },

        delayAxisTooltip: {
          en: "X axis can be relative to the current expected position/time",
          he: `תצוגה יחסית - מציגה את מיקומי האוטובוסים בזמן אמת על קו אמצע ומאפשרת מבט על מידת ההקדמה או האיחור ביחס למתוכנן.
          \n
        תצוגה אבסולוטית - מציגה את המיקום המתוכנן על קו האמצע ומאפשרת לראות פערים ביחס לתכנון בצורה ברורה.
        \n`,
        },

        calendarTooltip: {
          en: "Select to display real-time data or data from the past week (updates in 30 sec intervals)",
          he: `בחר הצגת מידע בזמן אמת או מידע מהשבוע החולף.
          \n
          ניתן לצפות ב"שידור חוזר" של נסיעות שבוצעו בשבוע האחרון. 
          \n
          בוחרים תאריך בלוח השנה ואת השעה אשר ממנה נרצה לצפות בנסיעות בדיעבד. 
          \n
          בחירת נסיעה תפתח את חלון ה"גרף מרחב-זמן" אשר יאפשר צפייה בנסיעות שבוצעו לפני השעה הנבחרת.
          \n`,
        },

      },

      ridership: {
        pageTitles: {
          stops: {
            en: "Stop Ridership",
            he: "נוסעים בתחנות",

          },
          routes: {
            en: "Route Ridership",
            he: "נוסעים בקווים",
          },
        },
        buttons: {
          getStopDetails:
          {
            en: "Get Stop Details",
            he: "הצג פרטי תחנה",
          },

          getRouteDetails:
          {
            en: "Get Route Details",
            he: "הצג פרטי קו",
          },
          select:
          {
            en: "Select",
            he: "בחר",
          },

          selectStop:
          {
            en: "Select Stop",
            he: "בחר תחנה",
          },

          selectRoute:
          {
            en: "Select Route",
            he: "בחר קו",
          },

        },

        searchTitles: {
          stops: {
            en: "Search Stop",
            he: "חיפוש תחנה",

          },
          routes: {
            en: "Search Route",
            he: "חיפוש קו",
          },
        },
        dataSpanExplain: {
          en: "The data presented are from the period spanning April through June 2024.",
          he: "המידע המוצג מתייחס לחודשים אפריל-יוני 2024.",
        },
        identityCards: {
          stops: {
            chartTitle: {
              en: "Bus Frequency",
              he: "תדירות עצירות בתחנה",

            },
            numberOfLines: {
              en: "Number of Lines",
              he: "מספר קווים בתחנה",
            },
            dailyRoutes: {
              en: "Number of Daily Route Stops",
              he: "מספר עצירות יומי",
            },
            peakRoutesHour: {
              en: "Peak Route Stop Hour",
              he: "שעת שיא עצירות",
            },

            dailyPickups: {
              en: "Number of Daily Pickups",
              he: "ממוצע עליות נוסעים יומי",
            },
            pickupPeakHour: {
              en: "Peak Pickup Hour",
              he: "שעת שיא עליות",
            },
            dailyDropoffs: {
              en: "Number of Daily Dropoffs",
              he: "שעת שיא ירידות",
            },
            pickupDropoffHour: {
              en: "Peak Dropoff Hour",
              he: "שעת שיא ירידות",
            },
            passengers: {
              en: "passengers",
              he: "נוסעים",
            },
            stops: {
              en: "stops",
              he: "עצירות",
            },
            chartPopup: {
              time: {
                en: "Time",
                he: "שעה",
              },
              freq: {
                en: "Frequency",
                he: "תדירות",
              }
            },


          },


          routes: {
            chartTitle: {
              en: "Route Frequency",
              he: "תדירות שעתית",

            },
            length: {
              en: "Length",
              he: "אורך הקו",
            },
            numberOfStops: {
              en: "Number of Stops",
              he: "מספר תחנות",
            },
            dailyTrips: {
              en: "Number of Daily Trips",
              he: "מספר נסיעות יומי",
            },
            peakFrequency: {
              en: "Peak Frequency",
              he: "תדירות שיא שעתית",
            },

            dailyPassengers: {
              en: "Number of Daily Passengers",
              he: "ממוצע נוסעים יומי",
            },
            pickupPeakHour: {
              en: "Peak Pickup Hourly",
              he: "ממוצע נוסעים שעתי",
            },
            pickupPeakTrip: {
              en: "Peak Pickup per Trip",
              he: "ממוצע נוסעים לנסיעה",
            },
            pickupVolumeHour: {
              en: "Peak Avg. Volume per Trip",
              he: "שיא ממוצע ממשיכים לנסיעה",
            },
            passengers: {
              en: "passengers",
              he: "נוסעים",
            },
            trips: {
              en: "trips",
              he: "נסיעות",
            },

          },

        },
        widgetTitles: {
          stops: {
            pickupDropoffChart: {
              en: "Average Daily Stop Ridership",
              he: "ממוצע עולים ויורדים יומי בתחנה",
            },
            ODMap: {
              en: "Daily Origin-Destination",
              he: "מוצאים ויעדים - ממוצע יומי",
            },
            routesMap: {
              en: "Routes in Stop",
              he: "קווים בתחנה",
            },

            routesList: {
              en: "Routes in Stop",
              he: "קווים בתחנה",
            },

          },


          routes: {
            pickupDropoffChart: {
              en: "Average Hourly Stop Ridership",
              he: "ממוצע עולים ויורדים שעתי",
            },
            volumeChart: {
              en: "Average Hourly Trip Volume Chart",
              he: "ממוצע ממשיכים שעתי לנסיעה",
            },
            routeMap: {
              en: "Average Hourly Trip Volume Map",
              he: "ממוצע ממשיכים שעתי לנסיעה",
            },

            ODMatrix: {
              en: "Line Hourly OD Matrix",
              he: "ממוצע מוצאים ויעדים שעתי",
            },

          },

        },

        widgetLegends: {
          stops: {
            pickupDropoffChart: {
              pickup: {
                en: "Pickup",
                he: "עולים",
              },
              dropoff: {
                en: "Dropoff",
                he: "יורדים",
              },
              transfer: {
                en: "Transfer",
                he: "מחליפים",
              },
            },
            ODMap: {
              destinations: {
                en: "Destinations",
                he: "יעדים",
              },
              origins: {
                en: "Origins",
                he: "מוצאים",
              },
              transfer: {
                en: "Transfer",
                he: "החלפות",
              },
            },


          },


          routes: {


          },

        },
        widgetPopups: {

          pickupDropoffChartPopup: {
            pickup: {
              en: "Avg. Pickup",
              he: "ממוצע עולים",
            },
            dropoff: {
              en: "Avg. Dropoff",
              he: "ממוצע יורדים",
            },
            transfer: {
              en: "Avg. Transfer",
              he: "ממוצע מחליפים",
            },
            stop: {
              en: "Stop",
              he: "תחנה",
            },
            time: {
              en: "Time",
              he: "שעה",
            },
          },
          routeVolumeChartPopup: {
            time: {
              en: "Time",
              he: "ממוצע עולים",
            },
            volume: {
              en: "Avg. Volume",
              he: "ממוצע ממשיכים",
            },

          },
          ODMap: {
            time: {
              en: "Time",
              he: "שעה",
            },
            pickupLocation: {
              en: "Pickup Location",
              he: "ישוב מוצא",
            },
            dropoffLocation: {
              en: "Dropoff Location",
              he: "ישוב יעד",
            },
            passengers: {
              en: "Avg. Number of Passengers",
              he: "ממוצע נוסעים",
            },
          },






        },
        lightRailWarning: {
          en: "Light rail estimates are based on poor quality data, which significantly impacts their accuracy and reliability. These estimates should be interpreted with caution.",
          he: ".הערכות עולים ויורדים ברכבת הקלה מבוססים על מידע חסר באיכות נמוכה. אי לכך, רמת הדיוק של ההערכות המוצגות עשויה להיות נמוכה. של יש לפרש את התוצאות המוצגות בהתאם.",
        },



      },
      loginPageLang: {
        title: {
          en: "Sign In",
          he: "כניסה",

        },
        password: {
          en: "Password",
          he: "ססמא",

        },
        email: {
          en: "Email Address",
          he: 'דוא"ל',

        },
      },

      profilePageLang: {
        title: {
          en: "User Profile",
          he: "אזור אישי",

        },
        name: {
          en: "Name",
          he: "שם",

        },
        email: {
          en: "Email",
          he: 'דוא"ל',

        },
        updatePasswordTitle: {
          en: "Update Password",
          he: "שינוי ססמא",

        },
        updatePasswordInput: {
          en: "Enter New Password",
          he: "הקלד ססמא חדשה",

        },
        confirmPasswordInput: {
          en: "Confirm Password",
          he: "הקלד ססמא שוב",

        },
        update: {
          en: "Update",
          he: "עדכן",

        },
        successMessage: {
          en: "Password Updates!",
          he: "הססמא עודכנה!",

        },

        errorMessage: {
          en: "Passwords do not match",
          he: "הססמאות אינן תואמות",

        },
        passwordMessages: {
          length: {
            en: "Password must be at least 8 characters long",
            he: "על הססמא להכיל לפחות 8 תווים",
          },
          digits: {
            en: "Password must contain at least one digit",
            he: "על הססמא להכיל לפחות ספרה אחת",
          },
          characters: {
            en: "Password must contain at least one character",
            he: "על הססמא להכיל אות אחת לפחות",
          },
          english: {
            en: "Password must contain at least one character",
            he: "על הססמא להכיל אותיות באנגלית בלבד",
          },
        }
      },
      noDataMessageLang: {
        en: "Loading... Please Wait",
        he: "המידע בדרך, נא להמתין...",

      },
      aboutButton: {
        buttonTitle: {
          en: "About",
          he: "אודות",

        },
        buttonText: {
          en: "",
          he: `המידע המוצג מבוסס על נתוני  SIRI - Service Interface for Real time Information שמשדר משרד התחבורה.
              המידע המוצג כולל את כלל התחבורה הציבורית בארץ, ללא הרכבת הקלה בירושלים.
              \n
              זמינות הנתונים תלויה בשידורם התקין ע"י מרכז נתוני זמן אמת במשרד התחבורה ובקליטת GPS ברכבים.
              \n
              המידע המוצג עבר תהליך עיבוד, ניקוי והעשרה ע"י מערכת City Pulse לטובת שיפור רמת האמינות והאיכות.
              \n`,

        },


      },
      serviceInsightsLang: {
        views: {
          stops:
          {
            maimTitle: {
              en: "Stops View",
              he: "תחנות",
            },
            features: {
              transferHotspots: {
                title: {
                  en: "Transfer Hotspots",
                  he: 'מוקדי מעבר',

                },
                tooltips: {
                  stops: {
                    'stop_code': {
                      en: "Stop Code",
                      he: "מזהה תחנה",

                    },
                    'stop_name': {
                      en: "Stop Name",
                      he: "שם תחנה",

                    },
                    'stop_desc': {
                      en:'Stop Desc',
                      he: "תאור תחנה",

                    },
                    'muni_name_heb': {
                      en: "Locality",
                      he: "יישוב",

                    },
                    'muni_name': {
                      en: "Locality",
                      he:"ישוב",

                    },
                    'daily_pickup_switch': {
                      en: "Daily Pickup Transfers",
                      he: "מספר החלפות יומי (עליה)",

                    },
                    'daily_dropoff_switch': {
                      en: "Daily Dropoff Transfers",
                      he: "מספר החלפות יומי (ירידה)",

                    },
                    'daily_freq': {
                      en: "Daily Stops",
                      he: "תדירות עצירות יומית",

                    },
                    'sum_pickup': {
                      en: "Total pickups",
                      he: 'סה"כ עולים',

                    },
                    'sum_dropoff': {
                      en: "Total Dropoffs",
                      he:'סה"כ יורדים',

                    },
                   
                    'stop_type': {
                      en: "Stop Type",
                      he: "סוג תחנה",

                    },

                  },
                  transfers:{
                    'dropoff_stopcode':  {
                      en: "Dropoff Stop Code",
                      he: "מזהה תחנת ירידה",

                    },
                    
                    'next_stopcode':  {
                      en: "Dropoff Stop Code",
                      he: "מזהה תחנת עליה",

                    },
                    'h_00':  {
                      en: "00:00 AM",
                      he: "00:00",

                    },
                    'h_01':  {
                      en:"01:00 AM",
                      he: "01:00",

                    },
                    'h_02':  {
                      en: "02:00 AM",
                      he: "02:00",

                    },
                    'h_03':  {
                      en: "03:00 AM",
                      he: "03:00",

                    },
                    'h_04':  {
                      en: "04:00 AM",
                      he: "04:00",

                    },
                    'h_05':  {
                      en: "05:00 AM",
                      he: "05:00",

                    },
                    'h_06':  {
                      en: "06:00 AM",
                      he: "06:00",

                    },
                    'h_07':  {
                      en: "07:00 AM",
                      he: "07:00",

                    },
                    'h_08':  {
                      en:"08:00 AM",
                      he:"08:00",

                    },
                    'h_09':  {
                      en: "09:00 AM",
                      he:"09:00",

                    },
                    'h_10':  {
                      en: "10:00 AM",
                      he: "10:00",

                    },
                    'h_11':  {
                      en: "11:00 AM",
                      he: "11:00",

                    },
                    'h_12':  {
                      en: "12:00 PM",
                      he:"12:00",

                    },
                    'h_13':  {
                      en: "01:00",
                      he:"13:00",

                    },
                    'h_14':  {
                      en: "02:00 PM",
                      he:"14:00",

                    },
                    'h_15':  {
                      en: "03:00 PM",
                      he: "15:00",

                    },
                    'h_16':  {
                      en: "04:00 PM",
                      he: "16:00",
                    },
                    'h_17':  {
                      en: "05:00 PM",
                      he:"17:00",

                    },
                    'h_18':  {
                      en: "06:00 PM",
                      he: "18:00",

                    },
                    'h_19':  {
                      en:"07:00 PM",
                      he: "19:00",

                    },
                    'h_20':  {
                      en: "08:00 PM",
                      he:"20:00",

                    },
                    'h_21':  {
                      en: "09:00 PM",
                      he: "21:00",
                    },
                    'h_22':  {
                      en: "10:00 PM",
                      he: "22:00",
                    },
                    'h_23':  {
                      en:"11:00 PM",
                      he:"23:00",

                    },
                    'daily_switches':  {
                      en: "Daily Transfers",
                      he: "מעברים יומיים",

                    },
                    
                    'hourly_switches':  {
                      en: "Transfers at",
                      he: "מעברים בשעה",

                    },
           
                  }
                },
                about: 
                {
                  aboutTitle:{
                    en: "About",
                    he: "אודות"
  
                  },
                aboutText:{
                  en: "",
                  he: `מוקדי מעבר בין תחנות
               
                   המידע מבוסס על חישוב נוסעים המחליפים בין אמצעי נסיעה בכל תחנה
            
                    ניתן לבחור ישוב מתוך תפריט הבחירה.
      
                     ניתן לבחור שטח תצוגה באופן דינמי על ידי ציור פוליגון באמצעות כפתורי הציור.
                
                      על מנת להציג קשרים בין תחנות ניתן להקליק על תחנה או מספר תחנות על מנת לייצר בחירה. לחיצה נוספת תחסיר את התחנה בסט הבחירה.
                    
                       ניתן גם לבחור מקבץ תחנות ע"י שרטוט שטח על המפה באמצעות כפתור ציור הפוליגון.
                 
                        ניתן לסנן את התחנות והחיצים המיצגים קשרים על ידי הסרגלים בתפריט.
                   
                         ניתן להשתמש בסרגל השעות על מנת לבחור את שעת התצוגה.`,

                },
              },
                largeAreaAlert: {
                  en: "The area you selected is too large. Please select a smaller area",
                  he: `השטח הנבחר גדול מידי. צמצמו את שטח הבחירה.`,

                },
                buttons: {
                  drawAreaOfInterest: {
                    en: "Draw an area of interest. Right click to finalize selection",
                    he: "צייר פוליגון על מנת לתחום את אזור התצוגה. ניתן לסיים את בחירת האזור על ידי לחיצה ימנית.",

                  },
                  drawStopsArea: {
                    en: "Select stops using a polygon. Right click to finalize selection",
                    he: "צייר פוליגון על מנת לבחור מקבץ תחנות",

                  },
                  clearSelection: {
                    en: "Clear selected stops",
                    he: "נקה בחירת תחנות",

                  },
                  threeD: {
                    en: "toggle 3D view",
                    he: "תצוגה תלת מימדית",

                  },
                },
                legend: {
                  mainTitle: {
                    en: "Transfer Hotspots",
                    he: 'מוקדי מעבר',

                  },
                  stopsTitle: {
                    en: "Daily stop transfers",
                    he: "מעברים יומיים בתחנה",

                  },
                  arrowsTitle: {
                    en: "Daily transfers between stops at ",
                    he: "מעברים בין תחנות בשעה ",

                  },

                },
                stopTypeSwitches: {
                  title: {
                    en: "Stop Type",
                    he: "סוג תחנה",

                  },
                  train: {
                    en: "Train",
                    he: "רכבת",

                  },
                  LRT: {
                    en: "Light Rail Train",
                    he: "רכבת קלה",

                  },
                  bus: {
                    en: "Bus",
                    he: "אוטובוס",

                  },
                  cable: {
                    en: "Cable Car",
                    he: "רכבל",

                  },
                  metronit: {
                    en: "Metronit",
                    he: "מטרונית",

                  },

                },
                sliders:
                {
                  stopsSlider:
                  {
                    title: {
                      en: "Daily stop transfers",
                      he: "מעברים יומיים בתחנה",

                    },
                    about: {
                      en: "",
                      he: "",

                    },

                  },
                  arrowsSlider:
                  {
                    title: {
                      en: "Daily transfers between stops",
                      he: "מעברים בין תחנות",

                    },
                    about: {
                      en: "",
                      he: "",

                    },

                  },
                  timeSlider:
                  {
                    title: {
                      en: "Time of Day",
                      he: 'שעה ביום',

                    },
                    about: {
                      en: "",
                      he: "",

                    },

                  },
                },
              }
            }

          },
          lines: {
            maimTitle: {
              en: "Lines View",
              he: "קווים",
            },
          },
          network:
          {
            maimTitle: {
              en: "Network View",
              he: "רשת",
            },
          },

        },








      },
    }
  },
  reducers: {
    change: (state) => {
      state.value = { ...state.value };
    },

    setCurrLang: (state, action) => {
      state.value.currLang = action.payload;
    },

  },
});

export const {

  setCurrLang
} = langSlice.actions;

export default langSlice.reducer;